import React, { useState, useEffect } from "react";
import { S } from "../../crm/theme";
import { fetchPnlEntries, fetchPnlEntriesV2, copyOldPnlEntries } from "../../crm/crmApi";
import { missingOld, summarize, sameSummary } from "../../crm/pnlMigrate";

// Settings > "Copy old entries". Copies the entries of the old P&L into the new storage.
// The old entries are only read. Pressing the button again never makes duplicates.

const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const LABEL = { Income: "Income", Expense: "Expenses", Transfer: "Owner's money (plus and minus)" };

export default function PnlCopyOld({ user }) {
  const [state, setState] = useState(null); // { oldList, newList }
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [oldList, newList] = await Promise.all([fetchPnlEntries(), fetchPnlEntriesV2()]);
      setState({ oldList, newList });
    } catch (e) {
      setMsg("Could not read the entries: " + e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const copyNow = async () => {
    const n = missingOld(state.oldList, state.newList).length;
    if (!window.confirm(`Copy ${n} old entr${n === 1 ? "y" : "ies"} into the new storage? The old entries stay as they are.`)) return;
    setBusy(true);
    try {
      const done = await copyOldPnlEntries(user);
      setMsg(`${done} entr${done === 1 ? "y" : "ies"} copied.`);
      await load();
    } catch (e) {
      setMsg("Copy failed, nothing was changed in the old entries: " + e.message);
    }
    setBusy(false);
  };

  if (!state) return <div style={{ color: "#7b82a0" }}>{msg || "Checking the entries…"}</div>;

  const todo = missingOld(state.oldList, state.newList);
  const copied = state.newList.filter((n) => n.source === "migrated");
  // Compare only the old entries that have a copy, so later new entries do not confuse the check.
  const copiedOldIds = new Set(copied.map((n) => n.oldId));
  const oldCopiedPart = summarize(state.oldList.filter((o) => copiedOldIds.has(o.id)));
  const newPart = summarize(copied);
  const allMatch = todo.length === 0 && sameSummary(oldCopiedPart, newPart);

  return (
    <div>
      <div style={S.section}>Copy old entries (one time)</div>
      <div style={{ color: "#7b82a0", fontSize: 13, marginBottom: 10 }}>
        The old entries are copied into the new storage. All income and expense entries go under “Company (common)”.
        Owner’s money entries get no project. The old entries are never changed.
      </div>
      {msg && <div style={S.msg}>{msg}</div>}

      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={S.th}>Check</th>
              <th style={S.th}>Old entries (copied ones)</th>
              <th style={S.th}>New storage</th>
              <th style={S.th}>Same?</th>
            </tr>
          </thead>
          <tbody>
            {["Income", "Expense", "Transfer"].map((k) => {
              const a = oldCopiedPart[k];
              const b = newPart[k];
              const same = a.count === b.count && a.amount === b.amount && a.gst === b.gst;
              return (
                <tr key={k}>
                  <td style={S.td}>{LABEL[k]}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{a.count} · {inr(a.amount)} · GST {inr(a.gst)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{b.count} · {inr(b.amount)} · GST {inr(b.gst)}</td>
                  <td style={{ ...S.td, color: same ? "#3ecf8e" : "#f87171", fontWeight: 700 }}>{same ? "Yes" : "No"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Old entries in total: <b>{state.oldList.length}</b> · Already copied: <b>{copied.length}</b> · Still to copy: <b>{todo.length}</b>
        </div>
        {allMatch && state.oldList.length > 0 && (
          <div style={{ color: "#3ecf8e", fontSize: 13, marginBottom: 10 }}>
            Everything is copied and the numbers match.
          </div>
        )}
        {todo.length > 0 && (
          <button style={S.btnBlue} disabled={busy} onClick={copyNow}>
            {busy ? "Copying…" : `Copy ${todo.length} old entr${todo.length === 1 ? "y" : "ies"} now`}
          </button>
        )}
        {todo.length === 0 && state.oldList.length === 0 && (
          <div style={{ color: "#7b82a0", fontSize: 13 }}>There are no old entries to copy.</div>
        )}
      </div>
    </div>
  );
}
